import type { AccountChat } from "@/lib/accountChatsParser"
import { normalizePlate } from "@/lib/plates"
import { compareDriverNames, type DriverNameOrder } from "@/lib/driverNameOrder"
import type { Car } from "@/reducers/carsSlice"
import type { Profile } from "@/reducers/profilesSlice"

/**
 * One vehicle on the crew board: the car, the chat that holds it and the
 * driver sitting in that chat's first seat.
 */
export type CrewRow = {
  car: Car
  sideNumber: string
  model: string
  chat: AccountChat | undefined
  driver: Profile | undefined
  doubleCrew: boolean
  // The route code in front of the vehicle in the chat's name ("D12"), or "".
  // The name is the only place a route lives.
  route: string
}

const DOUBLE_CREW_GROUP = "double_crew"

// Solid iCar's label: the vehicle's alias, its plate in brackets behind it.
const LABEL = /^(.*?)\s*\(([^()]*)\)\s*$/

const bare = (jid: string): string => jid.split("/")[0].toLowerCase()

const squash = (text: string): string =>
  text.split(/\s+/).filter(Boolean).join(" ")

/**
 * "C60 T/SPRINTER" out of "C60 T/SPRINTER (DX07168)", or "".
 *
 * Mirrors vehicle_alias() in account_config's crew_sheet_sync, so a chat named
 * here reads the same as one the sheet named: a label of any other shape, or
 * one whose bracket holds some other plate, has no alias.
 */
export const vehicleAlias = (car: Car): string => {
  const found = LABEL.exec(car.label)
  if (!found || normalizePlate(found[2]) !== normalizePlate(car.plates)) {
    return ""
  }
  const alias = squash(found[1])
  const token = normalizePlate(alias)
  return token && token !== normalizePlate(car.plates) ? alias : ""
}

export const driverDisplayName = (profile: Profile): string => {
  if (profile.firstName && profile.lastName) {
    return `${profile.firstName} ${profile.lastName}`
  }
  return profile.name || profile.jid
}

const driverNameParts = (driver: Profile | undefined): string[] => {
  if (!driver) return []
  if (driver.firstName || driver.lastName) {
    return [driver.firstName ?? "", driver.lastName ?? ""]
  }
  return [driver.name]
}

/** "ALIAS PLATE": how the vehicle read in a chat's name before the slashes. */
const vehicleLabel = (car: Car): string =>
  squash([vehicleAlias(car), car.plates || car.label].join(" "))

/** The vehicle in its chat's name: the alias alone, the plate only without one. */
export const vehicleName = (car: Car): string =>
  vehicleAlias(car) || squash(car.plates || car.label)

// Spaced, as an alias may hold a slash of its own ("C60 T/SPRINTER").
const SEPARATOR = " / "

/**
 * "ROUTE / ALIAS / First Last", always built from the car, driver and route
 * and never from the chat's stored name: upsert_muc keeps a name that differs
 * from the stored one as sent, which is what keeps the side number in front.
 */
export const crewChatName = (
  car: Car,
  driver: Profile | undefined,
  route = "",
): string =>
  [squash(route), vehicleName(car), squash(driverNameParts(driver).join(" "))]
    .filter(Boolean)
    .join(SEPARATOR)

// Where the vehicle starts in the name: chats named before the slashes carry
// "ALIAS PLATE", the rest the alias alone.
const vehicleAt = (car: Car, name: string): number => {
  const old = name.indexOf(vehicleLabel(car))
  return old >= 0 ? old : name.indexOf(vehicleName(car))
}

/** Whatever stands before the vehicle in the chat's name, or "". */
export const chatRoute = (car: Car, name: string): string => {
  const text = squash(name)
  const at = vehicleAt(car, text)
  return at > 0
    ? text
        .slice(0, at)
        .replace(/\s*\/\s*$/, "")
        .trim()
    : ""
}

/**
 * The chat's name with `route` in place of the old one. A chat whose driver
 * the board knows, or that has none, gets the full crew name; one seating a
 * driver without a profile keeps the rest of its name as it is.
 */
export const nameWithRoute = (row: CrewRow, route: string): string => {
  if (row.driver || !row.chat?.driverA) {
    return crewChatName(row.car, row.driver, route)
  }
  const name = squash(row.chat.name)
  const at = vehicleAt(row.car, name)
  if (at < 0) return crewChatName(row.car, undefined, route)
  return [squash(route), name.slice(at)].filter(Boolean).join(SEPARATOR)
}

export const carShortName = (row: CrewRow): string =>
  row.sideNumber || row.car.plates || row.car.label

const natural = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })

export const compareCrewRows = (a: CrewRow, b: CrewRow): number => {
  if (a.sideNumber && !b.sideNumber) return -1
  if (!a.sideNumber && b.sideNumber) return 1
  return natural(carShortName(a), carShortName(b))
}

export type CrewSortKey = "route" | "driver" | "sideNumber" | "model" | "plates"

export type CrewSort = { key: CrewSortKey; desc: boolean }

const isBlank = (row: CrewRow, key: CrewSortKey): boolean => {
  if (key === "driver") return !row.driver
  if (key === "plates") return !row.car.plates
  return !row[key]
}

/**
 * The board sorted by one column. Rows without a value in it stay at the
 * bottom either way; ties keep the side number order.
 */
export const sortCrewRows = (
  rows: CrewRow[],
  sort: CrewSort,
  nameOrder: DriverNameOrder,
): CrewRow[] => {
  const byDriver = compareDriverNames(nameOrder)
  const compare = (a: CrewRow, b: CrewRow): number => {
    switch (sort.key) {
      case "driver":
        return byDriver(a.driver!, b.driver!)
      case "sideNumber":
        return compareCrewRows(a, b)
      case "plates":
        // Character by character: a plate's digits are no number.
        return a.car.plates.localeCompare(b.car.plates, undefined, {
          sensitivity: "base",
        })
      default:
        return natural(a[sort.key], b[sort.key])
    }
  }
  return [...rows].sort((a, b) => {
    const blankA = isBlank(a, sort.key)
    const blankB = isBlank(b, sort.key)
    if (blankA !== blankB) return blankA ? 1 : -1
    const order = blankA ? 0 : compare(a, b)
    return (sort.desc ? -order : order) || compareCrewRows(a, b)
  })
}

const chatHoldingCar = (
  car: Car,
  chats: AccountChat[],
): AccountChat | undefined => {
  const holding = chats.filter((chat) => chat.carId === car.id)
  const doubleCrew = holding.find((chat) => chat.group === DOUBLE_CREW_GROUP)
  if (doubleCrew) return doubleCrew
  if (holding.length > 1) {
    console.warn(
      "crew board: several chats hold car",
      car.id,
      holding.map((chat) => chat.jid),
    )
  }
  return [...holding].sort((a, b) => a.jid.localeCompare(b.jid))[0]
}

const modelOf = (car: Car, alias: string): string => {
  if (alias) return alias.split(" ").slice(1).join(" ")
  return normalizePlate(car.label) !== normalizePlate(car.plates)
    ? car.label
    : ""
}

export const buildCrewRows = (
  cars: Car[],
  chats: AccountChat[],
  drivers: Profile[],
): CrewRow[] => {
  const driverByJid = new Map(drivers.map((d) => [bare(d.jid), d]))
  return (
    cars
      // "manual:..." records are what old chats saved as a hand-typed vehicle,
      // not a car in the fleet (see EditCrewModal's isManualCarId).
      .filter((car) => !car.id.startsWith("manual"))
      .map((car) => {
        const alias = vehicleAlias(car)
        const chat = chatHoldingCar(car, chats)
        return {
          car,
          sideNumber: alias.split(" ")[0] ?? "",
          model: modelOf(car, alias),
          chat,
          driver: chat?.driverA
            ? driverByJid.get(bare(chat.driverA))
            : undefined,
          doubleCrew: chat?.group === DOUBLE_CREW_GROUP,
          route: chat ? chatRoute(car, chat.name) : "",
        }
      })
      .sort(compareCrewRows)
  )
}

/** Every seated driver's bare jid to the row of the vehicle they drive. */
export const vehicleOfDriver = (
  rows: CrewRow[],
  chats: AccountChat[],
): Map<string, CrewRow> => {
  const rowByCar = new Map(rows.map((row) => [row.car.id, row]))
  const map = new Map<string, CrewRow>()
  for (const chat of chats) {
    const row = chat.carId ? rowByCar.get(chat.carId) : undefined
    if (!row || row.chat !== chat) continue
    for (const seat of [chat.driverA, chat.driverB]) {
      if (seat) map.set(bare(seat), row)
    }
  }
  return map
}

export const driversWithoutCar = (
  chats: AccountChat[],
  drivers: Profile[],
): Profile[] => {
  const seated = new Set<string>()
  for (const chat of chats) {
    if (!chat.carId) continue
    for (const seat of [chat.driverA, chat.driverB]) {
      if (seat) seated.add(bare(seat))
    }
  }
  return drivers.filter((driver) => !seated.has(bare(driver.jid)))
}

/**
 * One line of the board by route: the route and the vehicle driving it, or
 * none yet. `id` holds up while the vehicle changes, for a stable React key.
 */
export type RouteRow = {
  id: string
  route: string
  row: CrewRow | undefined
}

/**
 * The vhost's routes in natural order. A route on two vehicles reads as two
 * lines, and one the vhost's list dropped still shows, so that no vehicle's
 * route disappears from the board.
 */
export const buildRouteRows = (
  routes: string[],
  rows: CrewRow[],
): RouteRow[] => {
  const onRoute = rows.filter((row) => row.route)
  const codes = new Set([...routes, ...onRoute.map((row) => row.route)])
  return [...codes].sort(natural).flatMap((route): RouteRow[] => {
    const holding = onRoute.filter((row) => row.route === route)
    return holding.length
      ? holding.map((row, at) => ({ id: `${route}#${at}`, route, row }))
      : [{ id: `${route}#0`, route, row: undefined }]
  })
}

export const isRouteStaffed = ({ row }: RouteRow): boolean =>
  !!row?.chat?.driverA

export const isFreeVehicle = (row: CrewRow): boolean =>
  !row.route && !row.chat?.driverA && !row.doubleCrew

export const freeVehicles = (rows: CrewRow[]): CrewRow[] =>
  rows.filter(isFreeVehicle)

/** What to send to one vehicle's chat; see saveCrewChat. */
export type CrewWrite = {
  row: CrewRow
  name: string
  driverJid?: string
  clearDriver?: boolean
}

/**
 * The writes that put `route` on `target`, in order. The chat stays with its
 * vehicle, so the route and its driver travel to the target's chat. upsert_muc
 * takes the driver out of the old chat by itself; the old chat's name then
 * still has to lose the route. A route without a driver leaves the target's
 * in place: the same driver and vehicle moving on to their next route. Without
 * a `target` the route loses its vehicle, and a driver can't stay on a route
 * that has none.
 */
export const carMoveWrites = (
  route: string,
  from: CrewRow | undefined,
  target: CrewRow | undefined,
): CrewWrite[] => {
  if (!target) {
    if (!from) return []
    return [
      {
        row: from,
        name: crewChatName(from.car, undefined),
        ...(from.chat?.driverA ? { clearDriver: true } : {}),
      },
    ]
  }
  const driver = from?.driver
  const toTarget: CrewWrite = driver
    ? {
        row: target,
        name: crewChatName(target.car, driver, route),
        driverJid: driver.jid,
      }
    : { row: target, name: nameWithRoute(target, route) }
  if (!from) return [toTarget]
  return [
    toTarget,
    {
      row: from,
      // A driver without a profile has no name to write on the new chat, so
      // they stay where they are, with the rest of the old chat's name.
      name: driver
        ? crewChatName(from.car, undefined)
        : nameWithRoute(from, ""),
    },
  ]
}
